import { useState, useEffect, useCallback, useRef, MouseEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Avatar,
  Box,
  Badge,
  Tooltip,
  Divider,
  ListItemIcon,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogContent,
  TextField,
  List,
  ListItem,
  ListItemText,
  InputAdornment,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Search as SearchIcon,
  Warning as OverdueIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  AutoAwesome as AIIcon,
} from '@mui/icons-material';
import { useUser, useUIStore, useUnreadCount, useSelectedWorkspace, useWorkspaceProjects, useWorkspaceDashboard } from '@/store';
import { useAuth } from '@/hooks/useAuth';
import { ROUTES } from '@/router';
import { taskApi, aiApi } from '@/api/services';

const DRAWER_WIDTH = 260;

interface SearchResult {
  taskId: string;
  issueKey: string;
  title: string;
  status: string;
  projectName: string;
  projectKey: string;
}

/**
 * Header/AppBar component with global search
 */
export const Header: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const user = useUser();
  const unreadCount = useUnreadCount();
  const { handleLogout } = useAuth();
  const toggleSidebar = useUIStore((state) => state.toggleSidebar);
  const workspace = useSelectedWorkspace();
  const projects = useWorkspaceProjects();

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchorEl);

  useEffect(() => {
    setMenuAnchorEl(null);
  }, [location.pathname]);

  const dashboard = useWorkspaceDashboard();
  const overdueCount = (dashboard?.stats as any)?.overdueTasks || 0;

  // Search state
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [aiMode, setAiMode] = useState(false);
  const [aiResponse, setAiResponse] = useState<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Cmd+K shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
      if (e.key === 'Escape') {
        setSearchOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Search logic
  const searchTimeoutRef = useRef<any>(null);

  const handleSearch = useCallback(async (query: string) => {
    setSearchQuery(query);
    setAiResponse(null);
    if (!query || query.length < 2 || projects.length === 0) {
      setSearchResults([]);
      return;
    }

    // Debounce 300ms
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(async () => {
    setIsSearching(true);
    try {
      if (aiMode && projects.length > 0) {
        // AI-powered natural language search
        try {
          const project = projects[0]; // Use first project for AI context
          const result = await aiApi.searchWithAI(String(project.groupId), query);
          const filter = result.filter || {};

          // Now search with the AI-parsed filter
          const results: SearchResult[] = [];
          for (const proj of projects) {
            try {
              const tasks = await taskApi.getGroupTasks(proj.groupId as any);
              const matches = tasks.filter((t: any) => {
                let match = true;
                if (filter.status?.length) match = match && filter.status.some((s: string) => (t.taskStatus || '').toLowerCase().includes(s.toLowerCase()));
                if (filter.priority?.length) match = match && filter.priority.some((p: string) => (t.taskPriority || '').toLowerCase().includes(p.toLowerCase()));
                if (filter.keyword) match = match && ((t.taskTitle || '') + ' ' + (t.taskDescription || '')).toLowerCase().includes(filter.keyword.toLowerCase());
                return match;
              });
              for (const t of matches.slice(0, 5)) {
                results.push({
                  taskId: t.taskId as string,
                  issueKey: (t as any).issueKey || '',
                  title: t.taskTitle || '',
                  status: t.taskStatus || '',
                  projectName: proj.groupName,
                  projectKey: proj.projectKey || '',
                });
              }
              if (results.length >= 10) break;
            } catch { continue; }
          }
          setSearchResults(results.slice(0, 10));
          if (results.length === 0) {
            setAiResponse(`AI understood: "${query}" but found no matching issues.`);
          }
        } catch {
          // Fall back to regular search on AI error
          setAiResponse('AI search unavailable. Showing keyword results.');
          await doKeywordSearch(query);
        }
      } else {
        await doKeywordSearch(query);
      }
    } catch {
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
    }, 300);
  }, [projects, aiMode]);

  const doKeywordSearch = useCallback(async (query: string) => {
    const results: SearchResult[] = [];
    const q = query.toLowerCase();
    for (const project of projects) {
      try {
        const tasks = await taskApi.getGroupTasks(project.groupId as any);
        const matches = tasks.filter((t: any) =>
          (t.issueKey || '').toLowerCase().includes(q) ||
          (t.taskTitle || '').toLowerCase().includes(q) ||
          (t.taskDescription || '').toLowerCase().includes(q)
        );
        for (const t of matches.slice(0, 5)) {
          results.push({
            taskId: t.taskId as string,
            issueKey: (t as any).issueKey || '',
            title: t.taskTitle || '',
            status: t.taskStatus || '',
            projectName: project.groupName,
            projectKey: project.projectKey || '',
          });
        }
        if (results.length >= 10) break;
      } catch { continue; }
    }
    setSearchResults(results.slice(0, 10));
  }, [projects]);

  const handleSearchResultClick = (result: SearchResult) => {
    setSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
    if (workspace?.workspaceSlug && result.issueKey) {
      navigate(`/w/${workspace.workspaceSlug}/issues/${result.issueKey}`);
    }
  };

  const handleProfileMenuOpen = (e: MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(e.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleProfileClick = () => {
    setMenuAnchorEl(null);
    navigate(ROUTES.PROFILE);
  };

  const handleLogoutClick = () => {
    setMenuAnchorEl(null);
    handleLogout();
  };

  const handleNotificationsClick = () => {
    navigate(ROUTES.NOTIFICATION);
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
          bgcolor: 'background.paper',
          color: 'text.primary',
        }}
      >
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={toggleSidebar}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Search button */}
          <Box
            onClick={() => setSearchOpen(true)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 0.75,
              borderRadius: 1,
              border: 1,
              borderColor: 'divider',
              cursor: 'pointer',
              flex: 1,
              maxWidth: 400,
              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
            }}
          >
            <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
              Search issues...
            </Typography>
            <Typography variant="caption" color="text.disabled" sx={{
              border: 1, borderColor: 'divider', borderRadius: 0.5, px: 0.75, py: 0.25, fontSize: '0.65rem',
            }}>
              {navigator.platform.includes('Mac') ? '⌘K' : 'Ctrl+K'}
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Toggle dark mode">
              <IconButton color="inherit" onClick={() => window.dispatchEvent(new CustomEvent('theme:toggle'))}>
                {localStorage.getItem('theme') === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
            </Tooltip>
            {overdueCount > 0 && (
              <Tooltip title={`${overdueCount} overdue issue${overdueCount > 1 ? 's' : ''}`}>
                <Chip
                  icon={<OverdueIcon sx={{ fontSize: 14 }} />}
                  label={overdueCount}
                  size="small"
                  color="error"
                  variant="outlined"
                  sx={{ height: 28, cursor: 'pointer' }}
                  onClick={() => {
                    if (workspace?.workspaceSlug) {
                      navigate(`/w/${workspace.workspaceSlug}`);
                    }
                  }}
                />
              </Tooltip>
            )}
            <Tooltip title="Notifications">
              <IconButton color="inherit" onClick={handleNotificationsClick}>
                <Badge badgeContent={unreadCount} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>

            <Tooltip title="Account">
              <IconButton
                onClick={handleProfileMenuOpen}
                size="small"
                sx={{ ml: 1 }}
              >
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                  {user?.userName ? getInitials(user.userName) : <PersonIcon />}
                </Avatar>
              </IconButton>
            </Tooltip>

            <Menu
              anchorEl={menuAnchorEl}
              id="account-menu"
              open={menuOpen}
              onClose={handleMenuClose}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              PaperProps={{
                elevation: 3,
                sx: { mt: 1.5, minWidth: 200 },
              }}
              keepMounted={false}
            >
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="subtitle1" fontWeight="medium">{user?.userName || 'User'}</Typography>
                <Typography variant="body2" color="text.secondary">{user?.userEmail || ''}</Typography>
              </Box>
              <Divider />
              <MenuItem onClick={handleProfileClick}>
                <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
                Profile
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogoutClick}>
                <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Search Dialog */}
      <Dialog
        open={searchOpen}
        onClose={() => { setSearchOpen(false); setSearchQuery(''); setSearchResults([]); setAiResponse(null); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { position: 'fixed', top: '15%', m: 0 } }}
      >
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
            <TextField
              inputRef={searchInputRef}
              autoFocus
              fullWidth
              placeholder={aiMode ? "Ask in natural language... e.g. 'show high priority bugs'" : "Search issues by key, title, or description..."}
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {isSearching ? <CircularProgress size={18} /> : (aiMode ? <AIIcon color="primary" /> : <SearchIcon />)}
                  </InputAdornment>
                ),
                sx: { fontSize: '1rem', py: 0.5 },
              }}
              sx={{ '& .MuiOutlinedInput-notchedOutline': { border: 'none' } }}
            />
            <Tooltip title={aiMode ? 'Switch to keyword search' : 'Switch to AI search'}>
              <IconButton
                onClick={() => { setAiMode(!aiMode); setSearchResults([]); setAiResponse(null); }}
                sx={{ mr: 1, color: aiMode ? 'primary.main' : 'text.secondary' }}
                size="small"
              >
                <AIIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {searchResults.length > 0 && (
            <List sx={{ maxHeight: 400, overflow: 'auto', p: 0 }}>
              {searchResults.map((result) => (
                <ListItem
                  key={result.taskId}
                  button
                  onClick={() => handleSearchResultClick(result)}
                  sx={{ px: 2, py: 1.5, '&:hover': { bgcolor: 'action.hover' } }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" color="primary" fontWeight={700} sx={{ minWidth: 70 }}>
                          {result.issueKey}
                        </Typography>
                        <Typography variant="body2" noWrap>
                          {result.title}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                        <Chip label={result.projectName} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                        <Chip label={result.status} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}

          {aiResponse && (
            <Box sx={{ px: 2, py: 1, bgcolor: 'action.hover' }}>
              <Typography variant="caption" color="text.secondary">
                <AIIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                {aiResponse}
              </Typography>
            </Box>
          )}

          {searchQuery.length >= 2 && searchResults.length === 0 && !isSearching && !aiResponse && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">No issues found for "{searchQuery}"</Typography>
            </Box>
          )}

          {searchQuery.length < 2 && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {aiMode ? 'Ask a question like "show overdue tasks" or "high priority bugs assigned to me"' : 'Type at least 2 characters to search'}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Header;
